import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const categoryThumbnails: Record<string, string> = {
  "Shoes": "https://readdy.ai/api/search-image?query=Single%20pair%20of%20trendy%20sneakers%20on%20light%20gray%20concrete%20surface%2C%20natural%20window%20light%2C%20clean%20minimalist%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-shoes-jun27&orientation=squarish",
  "Hoodies/Sweaters": "https://readdy.ai/api/search-image?query=Cozy%20knit%20sweater%20on%20rustic%20wooden%20bench%2C%20warm%20afternoon%20sunlight%2C%20minimalist%20flat%20lay%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-hoodies-jun27&orientation=squarish",
  "T-Shirts": "https://readdy.ai/api/search-image?query=Stylish%20graphic%20t-shirt%20on%20white%20marble%20surface%2C%20bright%20studio%20lighting%2C%20clean%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-tshirts-jun27&orientation=squarish",
  "Jackets": "https://readdy.ai/api/search-image?query=Stylish%20leather%20jacket%20on%20dark%20slate%20surface%2C%20dramatic%20side%20lighting%2C%20editorial%20fashion%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-jackets-jun27&orientation=squarish",
  "Pants/Shorts": "https://readdy.ai/api/search-image?query=Tailored%20trousers%20on%20light%20oak%20floor%2C%20soft%20diffused%20daylight%2C%20minimalist%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-pants-jun27&orientation=squarish",
  "Headwear": "https://readdy.ai/api/search-image?query=Fashionable%20cap%20on%20textured%20stone%20surface%2C%20golden%20hour%20sunlight%2C%20clean%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-headwear-jun27&orientation=squarish",
  "Sets": "https://readdy.ai/api/search-image?query=Coordinated%20matching%20outfit%20on%20pale%20blush%20backdrop%2C%20soft%20editorial%20lighting%2C%20fashion%20flat%20lay%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-sets-jun27&orientation=squarish",
  "Underwear/Underpants": "https://readdy.ai/api/search-image?query=Premium%20cotton%20basics%20on%20soft%20linen%20fabric%2C%20gentle%20morning%20light%2C%20minimalist%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-underwear-jun27&orientation=squarish",
  "Jersey": "https://readdy.ai/api/search-image?query=Sports%20jersey%20on%20charcoal%20concrete%2C%20dramatic%20stadium%20lighting%2C%20editorial%20sports%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-jersey-jun27&orientation=squarish",
  "Accessories": "https://readdy.ai/api/search-image?query=Watches%20and%20sunglasses%20on%20reflective%20black%20glass%2C%20luxury%20spotlight%2C%20elegant%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-accessories-jun27&orientation=squarish",
  "Bags": "https://readdy.ai/api/search-image?query=Stylish%20leather%20bag%20on%20reclaimed%20wood%2C%20natural%20daylight%20from%20window%2C%20clean%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-bags-jun27&orientation=squarish",
  "Belts": "https://readdy.ai/api/search-image?query=Rolled%20leather%20belt%20on%20warm%20terracotta%20tile%2C%20Mediterranean%20sunlight%2C%20minimalist%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-belts-jun27&orientation=squarish",
  "Perfumes": "https://readdy.ai/api/search-image?query=Elegant%20glass%20perfume%20bottle%20on%20polished%20black%20marble%2C%20studio%20lighting%2C%20luxury%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-perfumes-jun27&orientation=squarish",
  "Other Stuff": "https://readdy.ai/api/search-image?query=Assorted%20lifestyle%20products%20on%20bamboo%20mat%2C%20soft%20diffused%20overhead%20light%2C%20clean%20product%20photography%2C%20no%20text%20no%20people&width=80&height=80&seq=nav-thumb-other-jun27&orientation=squarish",
};

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const textColor = scrolled ? "text-foreground-700" : "text-background-50/90";
  const textHover = scrolled ? "hover:text-foreground-950" : "hover:text-background-50";
  const logoColor = scrolled ? "text-foreground-950" : "text-background-50";
  const bgClass = scrolled
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
    : "bg-foreground-950/25 backdrop-blur-md";
  const iconColor = scrolled ? "text-foreground-950" : "text-background-50";

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${bgClass}`}>
      <div className="w-full px-4 md:px-10 py-3.5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group cursor-pointer">
          <span className={`text-xl md:text-2xl font-heading font-bold tracking-tight transition-colors duration-300 ${logoColor}`}>
            itaobuy<span className="text-secondary-500">.</span>cam
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
          >
            Home
          </Link>
          <Link
            to="/shopping-guide"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
          >
            Shopping Guide
          </Link>
          <Link
            to="/itaobuyspreadsheet-guide"
            className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
          >
            Guide
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}
            >
              Categories
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-72 bg-background-50 border border-background-200/70 rounded-xl py-3 shadow-sm animate-scale-in">
                <div className="max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={categoryThumbnails[cat.name]}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
              scrolled
                ? "bg-accent-500/20 text-foreground-950 hover:bg-accent-500/30"
                : "bg-background-50/20 text-background-50 hover:bg-background-50/30"
            }`}
          >
            Buy Now
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${iconColor}`}
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-5 py-5 animate-fade-in max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <Link
              to="/"
              className="text-sm font-semibold text-foreground-900 py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
              Home
            </Link>
            <Link
              to="/shopping-guide"
              className="text-sm font-semibold text-foreground-900 py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line text-secondary-500 w-5 h-5 flex items-center justify-center"></i>
              Shopping Guide
            </Link>
            <Link
              to="/itaobuyspreadsheet-guide"
              className="text-sm text-foreground-700 py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-guide-line text-foreground-500 w-5 h-5 flex items-center justify-center"></i>
              Guide
            </Link>

            <div className="border-t border-background-200/70 pt-3 mt-1">
              <p className="text-xs text-foreground-500 font-semibold mb-2 px-3 uppercase tracking-wider">Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-xs text-foreground-700 hover:text-foreground-950 py-2 px-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1.5"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-6 h-6 rounded overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={categoryThumbnails[cat.name]} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-950 bg-accent-500/20 text-center px-5 py-3 rounded-full hover:bg-accent-500/30 transition-colors cursor-pointer mt-2"
            >
              Buy Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}